import { Component, ViewChild, Input, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { finalize, takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs/Subject';
import { Observable } from 'rxjs';
import { Chart } from 'chart.js';
import * as ChartAnnotation from 'chartjs-plugin-annotation';
import { NbThemeService, NbColorHelper } from '@nebular/theme';
import { PatientData } from '../../../../@core/interfaces/common/CalorieFriend/patients';
import {
  BiometricHistoryData,
  BiometricHistoryView,
} from '../../../../@core/interfaces/common/CalorieFriend/biometric-history';
import { GridData } from '../../../../@core/interfaces/common/grid-data';
import {
  AssessmentData,
  AssessmentView,
} from '../../../../@core/interfaces/common/CalorieFriend/assessment';
import { Utility } from '../../../../@core/utils/utility';
import { ChangeDetectorRef } from '@angular/core';
import { PatientBiometricHistoryListComponent } from '../biometric-history-list/biometric-history-list.component';

/**
 * Component for listing the Biometric History items and Assessment items of the Patient
 * as Graphs and Tables on the Patient Sheet View page
 */
@Component({
  selector: 'app-patient-progress',
  templateUrl: './patient-progress.component.html',
  styleUrls: [
    './patient-progress.component.scss',
    '../../global.component.scss',
  ],
})
export class PatientProgressComponent implements OnInit, OnDestroy {
  @Input() patientId: number;
  @Input() notificationToken: string;

  @ViewChild('biometricHistoryList', { static: false })
  biometricHistoryList?: PatientBiometricHistoryListComponent;

  private readonly unsubscribe$ = new Subject<void>();
   ///////////////////////////////////////////////////////
   AddPaitentLabel = 'Add New Patient';
   UpdatePaitentLabel = 'Update Patient';

   // SavePatientLabel = "Save measurements";
   GeneralInfoLabel = 'General Info';
   BiometricInfoLabel = 'BioMetric Info';
   PreferencesLabel = 'Preferences';
   PreferredUnitLabel = 'Preferred Units';
   HeightLabel = 'Height';
   WeightLabel = 'Weight';
   BodyFatLabel = 'Body Fat %';
   BMILabel = 'BMI';
   SourceLabel = 'Source';
   MeasureDateLabel = 'Date of Measure';
   WeeklyAssessmentLabel = 'Weekly CheckIn';

   StartDateLabel = 'Start Date';
   EndDateLabel = 'End Date';
   MealPlanComplianceLabel = 'Meal Plan Compliance';
   EnergyLevelLabel = 'Energy Level';
   StressLevelLabel = 'Stress Level';
   SleepQualityLabel = 'Sleep Quality';

   Updated = 'Updated';
   ProblemCeatingPatient = 'A problem occurred creating the patient.';

   patientIsPreferredUnitImperial: boolean = false;

   // Bio History graph setting
   @ViewChild('chart1', { static: true }) _chart?: any;

   bioHistoryGraphData: any = null;
   biometricHistoryGraphDataSrc: Observable<GridData<BiometricHistoryView>> =
     null;
   bioHistoryGraphOptions: any;
   public isLoadingBiometricHistory: boolean = false;

   // Assessments Graph setting
   @ViewChild('chart2', { static: true }) _chart2?: any;

   @ViewChild('chart3', { static: true }) _chart3?: any;

   assessmentGraphDataSource: Observable<GridData<AssessmentView>> = null;
   assessmentGraphData: any = null;
   public isLoadingAssessmentsGraphData: boolean = false;
   assessmentGraphOptions: any;
   assessmentSecondGraphData: any = null;
   assessmentSecondGraphOptions: any;

   themeSubscription: any;

  constructor(
    private router: Router,
    private patientsService: PatientData,
    private biometricHistoryService: BiometricHistoryData,
    private assessmentService: AssessmentData,
    private theme: NbThemeService,
    protected cd: ChangeDetectorRef,
    route: ActivatedRoute,
  ) {
    this.patientId = +route.snapshot.paramMap.get('PatientId');
    this.notificationToken = route.snapshot.paramMap.get('NotificationToken');
  }

  /**
   * Initializes the component.
   * It creates the Biometric History Form and Assessment Form.
   * It loads the patient data.
   * It initializes the settings for the Biometric History table and the Assessment table of the Patient.
   * It initializes the settings for the Biometric History graph and the Assesmment graph.
   */
  ngOnInit(): void {
    this.loadPatientData();

    this.biometricHistoryGraphDataSrc =
      this.biometricHistoryService.AllHistoriesList(
        this.patientId,
        this.notificationToken,
      );

    // Biometric History graph initializing
    const namedChartAnnotation = ChartAnnotation;
    namedChartAnnotation['id'] = 'annotation';
    Chart.pluginService.register(namedChartAnnotation);
    this.refreshBioHistoryGraph();

    this.assessmentGraphDataSource = this.assessmentService.AllAssessmentsList(
      this.patientId,
      this.notificationToken,
    );

    // Assessments graphs initializing
    this.refreshAssessmentsGraph();

    // binding change message of the service
    this.patientsService.onUpdated$.subscribe((id) => {
      if (this.patientId === id) {
        this.loadPatientData();
      }
    });
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  loadPatientData(): void {
    if (!this.patientId && !this.notificationToken) return;

    this.patientsService
      .get(this.patientId, this.notificationToken)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((patient) => {
        this.patientIsPreferredUnitImperial = patient.isPreferredUnitImperial;

        this.refreshBioHistoryGraph();
        this.refreshAssessmentsGraph();
      });
  }

  refreshBioHistoryGraph(): void {
    this.isLoadingBiometricHistory = true;
    this.biometricHistoryGraphDataSrc
    .pipe(finalize(() => this.isLoadingBiometricHistory = false))
    .subscribe((data) => {
      this.themeSubscription = this.theme.getJsTheme().subscribe((config) => {
        const colors = config.variables;
        const chartjs: any = config.variables.chartjs;

        const labels = data.items.map((b) =>
          this.getLabelFromDate(b.measureDateTime),
        );
        let valuesWeight = data.items.map((b) => b.weight);
        const valuesBodyFat = data.items.map((b) => b.bodyFat);
        const valuesBMI = data.items.map((b) => b.bmi);

        if (this.patientIsPreferredUnitImperial) {
          valuesWeight = valuesWeight.map(
            (b) => Math.round((b / 0.453592) * 10) / 10,
          );
        }

        this.bioHistoryGraphData = {
          labels: labels,
          datasets: [
            {
              data: valuesWeight,
              label: 'Weight',
              fill: false,
              backgroundColor: '#3498db',
              borderColor: '#3498db',
              borderWidth: 6
            },
            {
              data: valuesBodyFat,
              label: 'Body Fat %',
              fill: false,
              backgroundColor: '#2ecc71',
              borderColor: '#2ecc71',
              borderWidth: 6
            },
            {
              data: valuesBMI,
              label: 'BMI',
              fill: false,
              backgroundColor: '#c2cc2e',
              borderColor: '#c2cc2e',
              borderWidth: 6
            },
          ],
        };

        this.bioHistoryGraphOptions = {
          responsive: true,
          maintainAspectRatio: false,
          animation: {
            duration: 0,
          },
          scales: {
            xAxes: [
              {
                gridLines: {
                  display: false,
                  color: chartjs.axisLineColor,
                },
                ticks: {
                  fontColor: chartjs.textColor,
                },
              },
            ],
            yAxes: [
              {
                gridLines: {
                  display: false,
                  color: chartjs.axisLineColor,
                },
                ticks: {
                  fontColor: chartjs.textColor,
                },
              },
            ],
          },
          legend: {
            labels: {
              fontColor: chartjs.textColor,
              usePointStyle : true,
              pointStyle: 'circle',
              lineWidth: 0,
            },
            position: 'bottom',
          },
          annotation: {
            annotations: [
              {
                type: 'line',
                mode: 'vertical',
                scaleID: 'x-axis-0',
                value: this.getFormattedTodayDate(),
                borderColor: 'orange',
                borderWidth: 2,
                label: {
                  enabled: true,
                  fontColor: 'orange',
                  content: 'Today',
                },
              },
            ],
          },
        };

        this.forceChartRefresh();
      });
    });
  }



  forceChartRefresh() {
    setTimeout(() => {
      this._chart.chart.canvas.style =
        'display: block; height: 250px; width: 100%;';
      this._chart.chart.update();
      this.cd.detectChanges();
    }, 30);
  }

  /**
   * Method to refresh Assessments graph
   */
  refreshAssessmentsGraph() {
    this.isLoadingAssessmentsGraphData = true;
    this.assessmentGraphDataSource
    .pipe(finalize(() => this.isLoadingAssessmentsGraphData = false))
    .subscribe((data) => {
      this.themeSubscription = this.theme.getJsTheme().subscribe((config) => {
        const colors = config.variables;
        const chartjs: any = config.variables.chartjs;

        const labels = data.items.map((b) => b.startDateString);
        let valuesWeight = data.items.map((b) => b.weight);
        const valuesBodyFat = data.items.map((b) => b.bodyFat);

        if (this.patientIsPreferredUnitImperial) {
          valuesWeight = valuesWeight.map(
            (b) => Math.round((b / 0.453592) * 10) / 10,
          );
        }

        const valuesMealPlanCommpliances = data.items.map(
          (b) => b.mealPlanCompliance,
        );
        const valuesEnergyLevels = data.items.map((b) => b.energyLevel);
        const valuesStressLevels = data.items.map((b) => b.stressLevel);
        const valuesSleepQuality = data.items.map((b) => b.sleepQuality);

        this.assessmentGraphData = {
          labels: labels,
          datasets: [
            {
              data: valuesWeight,
              label: 'Weight',
              fill: false,
              backgroundColor: '#3498db',
              borderColor: '#3498db',
              borderWidth: 6
            },
            {
              data: valuesBodyFat,
              label: 'Body Fat %',
              fill: false,
              backgroundColor: '#2ecc71',
              borderColor: '#2ecc71',
              borderWidth: 6
            },
          ],
        };

        this.assessmentGraphOptions = {
          responsive: true,
          maintainAspectRatio: false,
          animation: {
            duration: 0,
          },
          scales: {
            xAxes: [
              {
                gridLines: {
                  display: false,
                  color: chartjs.axisLineColor,
                },
                ticks: {
                  fontColor: chartjs.textColor,
                },
              },
            ],
            yAxes: [
              {
                gridLines: {
                  display: false,
                  color: chartjs.axisLineColor,
                },
                ticks: {
                  fontColor: chartjs.textColor,
                },
              },
            ],
          },
          legend: {
            labels: {
              fontColor: chartjs.textColor,
              usePointStyle : true,
              pointStyle: 'circle',
              lineWidth: 0,
            },
            position: 'bottom',
          },
          annotation: {
            annotations: [
              {
                type: 'line',
                mode: 'vertical',
                scaleID: 'x-axis-0',
                value: this.getFormattedTodayDate(),
                borderColor: 'orange',
                borderWidth: 2,
                label: {
                  enabled: true,
                  fontColor: 'orange',
                  content: 'Today',
                },
              },
            ],
          },
        };

        this.assessmentSecondGraphData = {
          labels: labels,
          datasets: [
            {
              data: valuesMealPlanCommpliances,
              label: 'Meal Plan Commpliance',
              fill: false,
              backgroundColor: '#3498db',
              borderColor: '#3498db',
              borderWidth: 6
            },
            {
              data: valuesEnergyLevels,
              label: 'Energy Level',
              fill: false,
              backgroundColor: '#2ecc71',
              borderColor: '#2ecc71',
              borderWidth: 6
            },
            {
              data: valuesStressLevels,
              label: 'Stress Level',
              fill: false,
              backgroundColor: '#c2cc2e',
              borderColor: '#c2cc2e',
              borderWidth: 6
            },
            {
              data: valuesSleepQuality,
              label: 'Sleep Quality',
              fill: false,
              backgroundColor: '#8f2ecc',
              borderColor: '#8f2ecc',
              borderWidth: 6
            },
          ],
        };

        this.assessmentSecondGraphOptions = {
          responsive: true,
          maintainAspectRatio: false,
          animation: {
            duration: 0,
          },
          scales: {
            xAxes: [
              {
                gridLines: {
                  display: false,
                  color: chartjs.axisLineColor,
                },
                ticks: {
                  fontColor: chartjs.textColor,
                },
              },
            ],
            yAxes: [
              {
                gridLines: {
                  display: false,
                  color: chartjs.axisLineColor,
                },
                ticks: {
                  fontColor: chartjs.textColor,
                },
              },
            ],
          },
          legend: {
            labels: {
              fontColor: chartjs.textColor,
              usePointStyle : true,
              pointStyle: 'circle',
              lineWidth: 0,
            },
            position: 'bottom',
          },
          annotation: {
            annotations: [
              {
                type: 'line',
                mode: 'vertical',
                scaleID: 'x-axis-0',
                value: this.getFormattedTodayDate(),
                borderColor: 'orange',
                borderWidth: 2,
                label: {
                  enabled: true,
                  fontColor: 'orange',
                  content: 'Today',
                },
              },
            ],
          },
        };

        //this.forceAssessmentChartRefresh();
        this.forceAssessmentSecondChartRefresh();
      });
    });
  }

  forceAssessmentChartRefresh() {
    setTimeout(() => {
      this._chart2.chart.canvas.style =
        'display: block; height: 250px; width: 100%;';
      this._chart2.chart.update();
      this.cd.detectChanges();
    }, 30);
  }

  forceAssessmentSecondChartRefresh() {
    setTimeout(() => {
      this._chart3.chart.canvas.style =
        'display: block; height: 250px; width: 100%;';
      this._chart3.chart.update();
      this.cd.detectChanges();
    }, 30);
  }

  getLabelFromDate(date: string): string {
    const items = date.split(', ')[1].split(' ');
    items.splice(2);
    const result = items.join(' ');
    return result;
  }

  getFormattedTodayDate(): string {
    const date = new Date();

    let month = (1 + date.getMonth()).toString();
    month = month.length > 1 ? month : '0' + month;

    let day = date.getDate().toString();
    day = day.length > 1 ? day : '0' + day;

    return month + '/' + day;
  }

  /**
   * Method to navigate to the Patients list page
   */
  Back() {
    this.router.navigate(['/pages/CalorieFriend/Patients']);
  }

  validateNumber = Utility.validateNumber;

  // Updated by Vinicius
  onAssessmentEdited() {
    this.refreshAssessmentsGraph();

    this.biometricHistoryList?.loadDataList(true);
    this.refreshBioHistoryGraph();
  }
}
